import { kycStatusMeta, type KycStatusTone } from "../constants/identityTiers.constants";
import type { Identity } from "../types/identities-api.types";

/**
 * Why an identity cannot be issued a card, as something renderable. Presentation only — the label is a key in
 * the `cards` namespace and the tone is the badge variant.
 */
export type HolderIneligibility = {
  tone: KycStatusTone;
  labelKey: string;
};

export type HolderEligibility = {
  eligible: boolean;
  /**
   * Null for an eligible holder. A row carries a badge only when it is NOT selectable, so a badge appearing
   * always means "this one is blocked" — an "approved" badge on every other row would turn the signal into
   * decoration.
   */
  reason: HolderIneligibility | null;
};

/**
 * Can this identity be issued a card, and if not, why?
 *
 * `POST /internal/cards` rejects on TWO independent axes and the operator needs to know which one was hit:
 * one 400 when `status !== ACTIVE`, a separate 400 when the KYC is not `VERIFIED`. Collapsing both into a
 * single "not verified" badge would send someone chasing a KYC link for a holder whose KYC is already
 * approved and whose card-provider registration is what is actually pending — the `VERIFIED` +
 * `PENDING_REVIEW` state, which is routine rather than a corner case.
 *
 * KYC is checked first because it is the axis the operator can act on: a pending KYC has a verification link
 * to send, while a pending review is something to wait out.
 *
 * Comparison is case-insensitive to tolerate API casing differences.
 */
export const resolveHolderEligibility = (identity: Identity): HolderEligibility => {
  const kyc = kycStatusMeta(identity.kyc_status);
  if (kyc.group !== "approved") {
    return { eligible: false, reason: { tone: kyc.tone, labelKey: kyc.labelKey } };
  }

  const status = (identity.status ?? "").toString().toUpperCase();
  if (status === "ACTIVE") return { eligible: true, reason: null };

  return {
    eligible: false,
    reason:
      status === "REJECTED"
        ? { tone: "destructive", labelKey: "identities_status_rejected" }
        : { tone: "warning", labelKey: "identities_status_pending_review" },
  };
};

/**
 * The same decision as a predicate, for the callers that only need the yes/no: the submit guard, the by-id
 * resolver, and the holders list on the identity detail page.
 */
export const isEligibleHolder = (identity: Identity): boolean => resolveHolderEligibility(identity).eligible;
